import React from 'react';
import PropTypes from 'prop-types';
import styles from './StickyActionBar.module.scss';

/* ────────────────────────────────────────────────────────────
 * StickyActionBar — bottom-pinned save/discard bar for long forms.
 *
 * Required on every form > 1 viewport of fields
 * (INVENTORY_UI_PLAN.md §6: AddItems, AddDiscount, StoreConfig,
 * AddNewStore, EditCategory).
 *
 * Default usage (driven by dirty-state from the hook):
 *
 *   <StickyActionBar
 *     visible={isDirty}
 *     message={hasErrors ? 'Fix errors before saving' : 'Unsaved changes'}
 *     left={<Button variant="ghost" onClick={onDiscard}>Discard</Button>}
 *     right={<Button onClick={onSave} loading={saving}>Save changes</Button>}
 *   />
 *
 * Motion (MOTION.md §4.2 / §4.10):
 *   - Enter: slide up from below + fade, --transition-modal.
 *   - Exit: slide down + fade, --transition-base.
 *   - Backdrop blur for the glass surface.
 * ──────────────────────────────────────────────────────────── */

const StickyActionBar = ({
  visible,
  message,
  left,
  right,
  variant,
  className,
}) => {
  if (!visible) return null;

  const classNames = [
    styles.bar,
    variant === 'danger' ? styles.barDanger : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classNames} role="region" aria-label="Save changes">
      <div className={styles.inner}>
        <div className={styles.left}>
          {message && <span className={styles.message}>{message}</span>}
          {left}
        </div>
        <div className={styles.right}>{right}</div>
      </div>
    </div>
  );
};

StickyActionBar.propTypes = {
  visible: PropTypes.bool,
  message: PropTypes.node,
  left: PropTypes.node,
  right: PropTypes.node,
  variant: PropTypes.oneOf(['default', 'danger']),
  className: PropTypes.string,
};

StickyActionBar.defaultProps = {
  visible: false,
  message: '',
  left: null,
  right: null,
  variant: 'default',
  className: '',
};

export default StickyActionBar;
